import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { clearAuth, readAuth, writeAuth } from './authStorage';
import { logoutCustomer } from '../services/api/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const initial = typeof window !== 'undefined' ? readAuth() : { token: '', customer: null };
  const [token, setToken] = useState(initial.token || '');
  const [customer, setCustomer] = useState(initial.customer || null);

  const isAuthenticated = !!String(token || '').trim();

  const setSession = useCallback((nextToken, nextCustomer) => {
    const t = String(nextToken || '').trim();
    setToken(t);
    setCustomer(nextCustomer || null);
    if (typeof window !== 'undefined') writeAuth({ token: t, customer: nextCustomer || null });
  }, []);

  const logout = useCallback(async () => {
    const t = String(token || '').trim();
    try {
      // Best-effort: invalidate server token/session.
      await logoutCustomer(t);
    } catch {
      // Still clear locally so user is logged out in UI.
    } finally {
      setToken('');
      setCustomer(null);
      if (typeof window !== 'undefined') clearAuth();
    }
  }, [token]);

  const value = useMemo(
    () => ({
      token,
      customer,
      isAuthenticated,
      setSession,
      logout,
    }),
    [token, customer, isAuthenticated, setSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

